import {useEffect, useMemo, useState} from "react";
import {ArrowLeftIcon, DocumentArrowUpIcon} from "@heroicons/react/24/outline";
import {Link} from "react-router-dom";
import Shell from "../../../../../components/specific/Shell.tsx";
import {User} from "../../../../../api/accessControl/User.ts";
import {BackendApi} from "../../../../../api/api.ts";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {ComboboxList} from "../../../../../components/general/ComboboxList.tsx";
import {fieldLabelClasses, fieldVariants} from "../../../../../components/general/fieldStyles.ts";
import {cn} from "../../../../../lib/utils.ts";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {JsonDropzone} from "./components/JsonDropzone.tsx";
import {ImportPreviewTables} from "./components/ImportPreviewTables.tsx";
import {ImportResultPanel} from "./components/ImportResultPanel.tsx";
import {buildPreview, parseImportFile, PreviewError, suggestClient} from "./preview.ts";
import {commitImport} from "./commit.ts";
import {Client, ImportFile, ImportResult} from "./types.ts";

export default function PartnerImportBookkeepingPage({currentUser}: {currentUser: User}) {
    const {dispatch} = useNotifications();
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);

    const [clients, setClients] = useState<Client[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [importFile, setImportFile] = useState<ImportFile | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);

    useEffect(() => {
        async function loadClients() {
            const response = await api.Partner__myClients(null, null);
            setClients(response.businesses.map((business) => ({id: business.id, name: business.name})));
        }
        void loadClients();
    }, [api]);

    const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;

    const preview = useMemo(
        () => (importFile && selectedClientId ? buildPreview(importFile, selectedClientId) : null),
        [importFile, selectedClientId],
    );

    const importableBookings = preview ? preview.bookings.filter((booking) => !booking.blocking).length : 0;
    const importableAssets = preview ? preview.assets.filter((asset) => !asset.blocking).length : 0;
    const nothingToImport = importableBookings === 0 && importableAssets === 0;

    async function handleFileSelect(nextFile: File) {
        try {
            const parsed = parseImportFile(await nextFile.text());
            setFile(nextFile);
            setImportFile(parsed);
            setResult(null);
            const detected = parsed.bookings.find((booking) => booking.business_name)?.business_name ?? null;
            const suggestion = suggestClient(detected, clients);
            setSelectedClientId(suggestion?.id ?? null);
        } catch (error) {
            const detail = error instanceof PreviewError ? error.message : "Onbekende fout";
            dispatch({title: "Bestand ongeldig", description: detail, type: "error"});
        }
    }

    function resetImport() {
        setFile(null);
        setImportFile(null);
        setResult(null);
    }

    async function handleConfirm() {
        if (!preview) return;
        setCommitting(true);
        try {
            const importResult = await commitImport(api, preview);
            setResult(importResult);
            const failed = importResult.bookingsFailed + importResult.assetsFailed;
            dispatch({
                title: failed === 0 ? "Import voltooid" : "Import deels mislukt",
                description: `${importResult.bookingsCreated} boekingen en ${importResult.assetsCreated} bedrijfsmiddelen aangemaakt.`,
                type: failed === 0 ? "success" : "error",
            });
        } finally {
            setCommitting(false);
        }
    }

    return (
        <Shell currentUser={currentUser} isLoading={false}>
            <div className="max-w-5xl">
                <Link
                    to="/"
                    className="inline-flex items-center p-2 rounded-lg hover:bg-gray-100 transition-colors mb-6"
                >
                    <ArrowLeftIcon className="h-5 w-5 text-gray-500 mr-2"/>
                    Dashboard
                </Link>

                <div className="flex items-center mb-8">
                    <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <DocumentArrowUpIcon className="h-8 w-8 text-gray-600"/>
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Beginbalans importeren</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Sleep een JSON-bestand, kies de klant en controleer de boekingen en
                            bedrijfsmiddelen voordat je importeert.
                        </p>
                    </div>
                </div>

                <div className="space-y-6">
                    <JsonDropzone
                        file={file}
                        onSelect={handleFileSelect}
                        onClear={resetImport}
                        disabled={committing}
                    />

                    {importFile && (
                        <div>
                            <label className={cn(fieldLabelClasses, "mb-2")}>Klant</label>
                            <ComboboxList
                                label=""
                                className={cn(fieldVariants(), "pr-12")}
                                emptySelectionMessage="Selecteer een klant"
                                disabled={committing}
                                onSelect={(option: {id: string; name: string}) =>
                                    setSelectedClientId(option?.id ?? null)
                                }
                                options={clients}
                                selected={clients.find((client) => client.id === selectedClientId) || null}
                                internalMapper={(item) => ({...item, displayName: item.name, id: item.id})}
                            />
                        </div>
                    )}

                    {preview && !result && (
                        <>
                            <ImportPreviewTables preview={preview}/>

                            <div className="flex items-center gap-3 pt-2">
                                <PrimaryButton
                                    title={committing ? "Bezig met importeren..." : "Importeren"}
                                    disabled={committing || nothingToImport}
                                    onClick={handleConfirm}
                                    confirmation={{
                                        title: "Import bevestigen",
                                        description: `Je staat op het punt ${importableBookings} boekingen en ${importableAssets} bedrijfsmiddelen te importeren voor ${
                                            selectedClient?.name ?? "de geselecteerde klant"
                                        }.`,
                                        acceptVerb: "Importeren",
                                    }}
                                />
                                <SecondaryButton title="Annuleren" onClick={resetImport} disabled={committing}/>
                            </div>
                        </>
                    )}

                    {importFile && !selectedClientId && (
                        <p className="text-sm text-amber-600">
                            Kies eerst een klant om het voorbeeld te tonen.
                        </p>
                    )}

                    {result && (
                        <>
                            <ImportResultPanel result={result}/>
                            <SecondaryButton title="Nieuwe import" onClick={resetImport}/>
                        </>
                    )}
                </div>
            </div>
        </Shell>
    );
}
